<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="quote.name + ' - ' + fixture.title"/>
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="activity_logs" />
    <div class="w-full mb-6">
        <FixtureHeader :fixture="fixture">
            <template v-slot:info>
                <p v-if="quote.buy_currency_id !== quote.sell_currency_id"><strong>Buy/Sell Currency:</strong> {{ quote.buy_currency.code }}/{{quote.sell_currency.code}} @ {{quote.exchange_rate }}</p>
            </template>
            <template v-slot:actions>
                <Link :href="route('frontend.fixture.quote.index', fixture.id)"
                    class="inline-flex items-center h-10 px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs uppercase text-black bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200">
                    <ArrowCircleLeftIcon class="-ml-0.5 mr-2 h-4 w-4"></ArrowCircleLeftIcon>
                    Back to Quotes
                </Link>
            </template>
        </FixtureHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="pb-5 border-b border-gray-200 flex justify-between items-center">
                                <div class="flex grid grid-cols-2 items-center">
                                    <div>
                                        <div class="flex items-end">
                                            <h3 class="flex text-lg leading-6 font-bold text-gray-900 mr-3">
                                                {{ quote.name }}
                                            </h3>
                                            <span class="text-sm font-medium">
                                                {{ quote.client.name }}
                                            </span>
                                            <div class="ml-3 flex-col">
                                                <span v-if="quote.status === 'Not Sent'" class="w-auto inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white">
                                                    {{ quote.status}}
                                                </span>
                                                <span v-if="quote.status === 'Sent to Client'" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-yellow-800">
                                                    {{ quote.status }}
                                                </span>
                                                <span v-if="quote.status === 'Confirmed'" class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-green-800">
                                                    {{ quote.status }}
                                                </span>
                                            </div>
                                        </div>
                                        <div v-if="restrictInvoice()" class="pt-3">
                                            <span class="text-sm font-bold text-red-500">
                                                The client of the quote does not belong to the trading company assigned to the fixture.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex text-right items-end">
                                    <Button @click="create"
                                        class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-grey focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        :disabled="(!quote.client.cmid || restrictInvoice())"
                                        :class="[
                                            restrictInvoice() ? 'bg-gray-600 cursor-not-allowed' : (quote.client.cmid ? 'bg-pitch-purple' : 'opacity-50 cursor-not-allowed')
                                        ]">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create New Invoice
                                    </Button>                                    
                                    <search-filter :show-filter="false" v-model:value="form.search" @reset="reset" class="mt-1"></search-filter>
                                </div>
                            </div>
                            <div v-if="!quote.client.cmid" class="rounded-md bg-red-100 p-4">
                                <div class="flex">
                                    <div class="flex-shrink-0">
                                        <InformationCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true"/>
                                    </div>
                                    <div class="ml-3 space-y-3">
                                        <p class="text-sm text-black-700">This client is missing a CMID. Please add a CMID to create an invoice.</p>
                                        <p class="text-sm text-black-700">Please email finance at <a href="mailto:sports.receipts@destinationsport.com" class="cursor-pointer underline">sports.receipts@destinationsport.com</a> to check client CMIDs that are in Unit 4.</p>
                                    </div>
                                </div>
                            </div>
                            <QuoteTabs />
                            <InvoiceList :invoices="invoices" :invoice_status_type="invoice" />
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
    <CreateInvoice ref="slideout" :service_lines="service_lines" :invoices="invoices" />
    <LinkInvoiceToSupplierInvoice />
    <AddServiceLines ref="slideout_two" />
</template>
<script>
    // Layouts
    import Authenticated from "@/Layouts/Authenticated";
    
    // Packages
    import pickBy from "lodash/pickBy";
    import debounce from "lodash/debounce";
    import mapValues from 'lodash/mapValues';
    import {Link, Head, usePage, useForm} from "@inertiajs/vue3";
    
    // Components
    import Button from "@/Components/Button";
    import QuoteTabs from "@/Components/QuoteTabs";
    import SearchFilter from "@/Components/SearchFilter";
    import InvoiceList from "@/Components/InvoiceList.vue";
    import FixtureHeader from "@/Components/FixtureHeader";
    import ValidationErrors from "@/Components/ValidationErrors";
    import CreateInvoice from "@/Components/SlideOuts/CreateInvoice";
    import AddServiceLines from "@/Components/SlideOuts/AddServiceLines";
    import LinkInvoiceToSupplierInvoice from "@/Components/SlideOuts/LinkInvoiceToSupplierInvoice";
    import Audit from "@/Components/Modal/Audit.vue";
    
    // Icons
    import {
        CogIcon,
        UsersIcon,
        PlusSmIcon,
        PencilIcon,
        FolderIcon,
        FolderAddIcon,
        CheckCircleIcon,
        ChevronDownIcon,
        DocumentAddIcon,
        ClipboardCopyIcon,
        ArrowCircleLeftIcon,
        DocumentDownloadIcon,
        InformationCircleIcon
    } from "@heroicons/vue/outline";

    export default {
        layout: Authenticated,
        components: {
            Link,
            Head,
            Button,
            CogIcon,
            QuoteTabs,
            UsersIcon,
            PlusSmIcon,
            PencilIcon,
            FolderIcon,
            InvoiceList,
            SearchFilter,
            FixtureHeader,
            CreateInvoice,
            FolderAddIcon,
            AddServiceLines,
            DocumentAddIcon,
            ChevronDownIcon,
            CheckCircleIcon,
            ValidationErrors,
            ClipboardCopyIcon,
            ArrowCircleLeftIcon,
            DocumentDownloadIcon,
            InformationCircleIcon,
            LinkInvoiceToSupplierInvoice,
            Audit
        },
  
        props: {
            quote: Object,
            search: Object,
            fixture: Object,
            invoices: Object,
            service_lines: Object,
            management_fee_invoices: Array,
            activity_logs: Array
        },
  
        data(props) {
            return {
                form: useForm({
                    search: this.search.search
                }),
                openModal: false
            }
        },
        
        methods: {
            create() {
                // this.$refs.slideout.updating = false;
                // this.$refs.slideout.client_id = this.fixture.client.id;
                this.$refs.slideout.open(this.quote);
            },
    
            reset() {
                this.form = mapValues(this.form, () => null)
            },

            restrictInvoice() {
                // Instantiate the fixture and quote details
                const fixtureDetails = this.fixture.client.trading_details;
                const quoteDetails = this.quote.client.trading_details;

                // Same trading entity – allow invoice
                if (fixtureDetails.id === quoteDetails.id) return false;

                // Instantiate standalone entity flags
                const fixtureStandalone = fixtureDetails.stand_alone_entity === 1;
                const quoteStandalone = quoteDetails.stand_alone_entity === 1;

                // Both are standalone – restrict invoice
                if (fixtureStandalone && quoteStandalone) return true;

                // One is standalone, one is not – check relationship
                if (fixtureStandalone && !quoteStandalone) {
                    return quoteDetails.related_trading_entity_id !== fixtureDetails.id;
                }

                if (!fixtureStandalone && quoteStandalone) {
                    return fixtureDetails.related_trading_entity_id !== quoteDetails.id;
                }

                // Neither is standalone – check if they share the same parent
                return fixtureDetails.related_trading_entity_id !== quoteDetails.related_trading_entity_id;
            }
        },
  
        watch: {
            form: {
                deep: true,
                handler: debounce(function() {
                    this.$inertia.get(route('frontend.fixture.quote.invoices.index', [this.fixture.id, this.quote.id]), pickBy(this.form), {preserveState: true, preserveScroll: true})
                }, 500),
            }
        },
    }
</script>
